import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  AgreementPickerComponent,
  SelectedAgreementOutput,
} from '@wilson/clients/core';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  ActivityCategoriesIdsMap,
  Label,
  OperationStatus,
  RoleAction,
  RolePermissionSubject,
  ShiftState,
} from '@wilson/interfaces';
import { LabelsModule } from '@wilson/labels';
import { UseLocalizedDateFormatDirective } from '@wilson/localization';
import { LocationSelectsComponent } from '@wilson/non-domain-specific/activities-helpers/components';
import { UsersSelectComponent } from '@wilson/non-domain-specific/account/account-helpers';
import { CollapsibleGroupingSectionComponent } from '@wilson/non-domain-specific/groupings';
import {
  CollapsibleActivitiesListComponent,
  CollapsibleShiftAttachmentsComponent,
  CollapsibleShiftFurtherInformationComponent,
  SetShiftProjectComponent,
} from '@wilson/non-domain-specific/shift-helpers/core';
import { OrganizationalUnitSelectComponent } from '@wilson/organizational-unit';
import { PipesModule } from '@wilson/pipes';
import { ShiftCategorySelectComponent } from '@wilson/shift-category';
import { TimePickerAutoFormatDirective } from '@wilson/non-domain-specific/time-picker-auto-format';
import {
  FetchProjectsIfNeededState,
  ProjectsState,
} from '@wilson/projects/state';
import { SectorState } from '@wilson/sectors';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Editor, NgxEditorModule } from 'ngx-editor';
import { map, merge, startWith, switchMap } from 'rxjs';
import {
  getShiftDurationMinutes,
  isBreakDurationOptionAvailable,
  isSimpleShiftActivities,
  validateExactBreakWithinShift,
} from '../helpers/is-simple-shift-activities';
import {
  SimplifiedShiftBreakOption,
  SimplifiedShiftDrawerFormService,
  SimplifiedShiftDrawerFormValue,
  SimplifiedShiftTierTwoSection,
} from '../simplified-shift-drawer-form.service';
import { SimplifiedDrawerShift } from '../types';
import {
  ActivityCategoriesState,
  TranslateActivityCategoryPipeModule,
} from '@wilson/activity-categories/data';

@Component({
  selector: 'wilson-information-section',
  templateUrl: './information-section.component.html',
  styleUrls: ['./information-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgreementPickerComponent,
    CollapsibleActivitiesListComponent,
    CollapsibleShiftAttachmentsComponent,
    CollapsibleShiftFurtherInformationComponent,
    CommonModule,
    FeatureFlagsModule,
    FontAwesomeModule,
    CollapsibleGroupingSectionComponent,
    LabelsModule,
    LocationSelectsComponent,
    NgxEditorModule,
    NzCollapseModule,
    NzDatePickerModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzTimePickerModule,
    NzTooltipModule,
    PipesModule,
    ReactiveFormsModule,
    OrganizationalUnitSelectComponent,
    SetShiftProjectComponent,
    ShiftCategorySelectComponent,
    TranslateModule,
    TimePickerAutoFormatDirective,
    UseLocalizedDateFormatDirective,
    UsersSelectComponent,
    TranslateActivityCategoryPipeModule,
  ],
})
export class InformationSectionComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  readonly shift = input.required<SimplifiedDrawerShift>();

  readonly refreshActivities = output<void>();
  readonly updatedNecessaryStay = output<boolean>();

  protected readonly formService = inject(SimplifiedShiftDrawerFormService);
  protected readonly form = this.formService.form;
  protected readonly can = inject(AbilityServiceSignal).can;
  protected readonly RoleAction = RoleAction;
  protected readonly RolePermissionSubject = RolePermissionSubject;
  protected readonly faInfoCircle = faInfoCircle;
  protected readonly isExpandedSignal = signal(true);
  protected readonly showAllTierTwoFields = signal(false);

  protected commentEditor!: Editor;
  protected planningCommentEditor!: Editor;

  protected readonly isSimpleActivitiesView = computed(
    () => this.formService.activitiesViewMode() === 'simple',
  );

  protected readonly areActivityFieldsDisabled = computed(() => {
    const selectedShift = this.shift();
    const hasUpdatePermission = this.can(
      RoleAction.Update,
      RolePermissionSubject.Shift,
    );
    if (!hasUpdatePermission) {
      return true;
    }

    if (
      selectedShift.state === ShiftState.AcceptedTimes ||
      selectedShift.state === ShiftState.SubmittedToPayrollProvider
    ) {
      return true;
    }

    return !selectedShift.activities.every(
      (activity) => activity.operationalStatus === OperationStatus.NotStarted,
    );
  });

  protected readonly activityCategories$ =
    this.form.controls.activityCategoryId.valueChanges.pipe(
      startWith(this.form.controls.activityCategoryId.value),
      switchMap((selectedCategoryId) =>
        this.store.select(
          ActivityCategoriesState.selectableCategoriesSortedByName(
            selectedCategoryId,
          ),
        ),
      ),
    );

  protected readonly projects$ = this.store.select(ProjectsState.projects);
  protected readonly sectorName$ = this.store
    .select(SectorState.firstSector)
    .pipe(map((sector) => sector?.name));

  constructor() {
    this.store.dispatch(new FetchProjectsIfNeededState());

    effect(() => {
      const isSimple = isSimpleShiftActivities(this.shift().activities);
      this.formService.syncActivitiesViewMode(isSimple, true);
    });

    effect(() => {
      const disabled = this.areActivityFieldsDisabled();
      const activityControls = [
        this.form.controls.shiftDate,
        this.form.controls.startTime,
        this.form.controls.endTime,
        this.form.controls.activityCategoryId,
        this.form.controls.breakOption,
        this.form.controls.breakStartTime,
        this.form.controls.breakEndTime,
        this.form.controls.location,
        this.form.controls.agreementId,
      ];

      activityControls.forEach((control) => {
        if (disabled && control.enabled) {
          control.disable({ emitEvent: false });
        } else if (!disabled && control.disabled) {
          control.enable({ emitEvent: false });
        }
      });
    });
  }

  ngOnInit(): void {
    this.commentEditor = new Editor();
    this.planningCommentEditor = new Editor();

    if (!this.can(RoleAction.Update, RolePermissionSubject.Shift)) {
      this.form.controls.userId.disable({ emitEvent: false });
    }

    merge(
      this.form.controls.startTime.valueChanges,
      this.form.controls.endTime.valueChanges,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const breakOption = this.form.controls.breakOption.value;
        if (
          (breakOption === 30 || breakOption === 60) &&
          !this.isBreakDurationOptionEnabled(breakOption)
        ) {
          this.form.controls.breakOption.markAsDirty();
          this.form.controls.breakOption.setValue(null);
          this.form.controls.breakStartTime.reset();
          this.form.controls.breakEndTime.reset();
        }
      });
  }

  ngOnDestroy(): void {
    this.commentEditor?.destroy();
    this.planningCommentEditor?.destroy();
  }

  protected isPromoted(section: SimplifiedShiftTierTwoSection): boolean {
    return this.formService.isTierTwoFieldPromoted(section);
  }

  protected switchToExtendedActivitiesView(): void {
    this.formService.switchToExtendedActivitiesView();
  }

  protected switchToSimpleActivitiesView(): void {
    this.formService.switchToSimpleActivitiesView();
  }

  protected handleFocus(field: keyof SimplifiedShiftDrawerFormValue): void {
    this.formService.setFocusedField(field);
  }

  protected handleBlur(): void {
    this.formService.setFocusedField(null);
  }

  protected toggleBreak(
    option: Exclude<SimplifiedShiftBreakOption, null>,
  ): void {
    if (this.areActivityFieldsDisabled()) {
      return;
    }

    if (
      (option === 30 || option === 60) &&
      !this.isBreakDurationOptionEnabled(option)
    ) {
      return;
    }

    const breakOptionControl = this.form.controls.breakOption;
    const previousOption = breakOptionControl.value;
    const nextOption = breakOptionControl.value === option ? null : option;

    if (nextOption === 'exact') {
      // Prefill silently first, then emit so autosave sees exact + from/to together.
      this.prefillExactBreakTimeFromCurrentSelection(previousOption);
      this.form.controls.breakStartTime.markAsDirty();
      this.form.controls.breakEndTime.markAsDirty();
      breakOptionControl.markAsDirty();
      breakOptionControl.setValue(nextOption);
      return;
    }

    breakOptionControl.markAsDirty();
    breakOptionControl.setValue(nextOption);
    this.form.controls.breakStartTime.reset();
    this.form.controls.breakEndTime.reset();
  }

  protected isBreakDurationOptionEnabled(option: 30 | 60): boolean {
    const startTime = this.form.controls.startTime.value;
    const endTime = this.form.controls.endTime.value;
    if (!startTime || !endTime) {
      return false;
    }

    return isBreakDurationOptionAvailable(
      getShiftDurationMinutes(startTime, endTime),
      option,
    );
  }

  protected getExactBreakErrorTranslationKey(): string | null {
    if (this.form.controls.breakOption.value !== 'exact') {
      return null;
    }

    const startTime = this.form.controls.startTime.value;
    const endTime = this.form.controls.endTime.value;
    if (!startTime || !endTime) {
      return null;
    }

    const error = validateExactBreakWithinShift({
      shiftStart: startTime,
      shiftEnd: endTime,
      breakStart: this.form.controls.breakStartTime.value,
      breakEnd: this.form.controls.breakEndTime.value,
    });

    switch (error) {
      case 'incomplete':
        return null;
      case 'invalid_range':
        return 'page.simplified_shift_drawer.break_invalid_range';
      case 'outside_shift':
        return 'page.simplified_shift_drawer.break_outside_shift';
      default:
        return null;
    }
  }

  private prefillExactBreakTimeFromCurrentSelection(
    previousOption: SimplifiedShiftBreakOption,
  ): void {
    if (
      this.form.controls.breakStartTime.value &&
      this.form.controls.breakEndTime.value
    ) {
      return;
    }

    const existingBreakWindow = this.getExistingBreakActivityWindow();
    if (existingBreakWindow) {
      this.form.controls.breakStartTime.setValue(existingBreakWindow.start, {
        emitEvent: false,
      });
      this.form.controls.breakEndTime.setValue(existingBreakWindow.end, {
        emitEvent: false,
      });
      return;
    }

    const durationMinutes = this.resolveBreakDurationMinutes(previousOption);
    if (!durationMinutes) {
      return;
    }

    const startTime = this.form.controls.startTime.value;
    const endTime = this.form.controls.endTime.value;
    if (!startTime || !endTime) {
      return;
    }

    const shiftStart = new Date(startTime);
    const shiftEnd = new Date(endTime);

    if (shiftEnd.getTime() <= shiftStart.getTime()) {
      shiftEnd.setDate(shiftEnd.getDate() + 1);
    }

    const midpoint = new Date(
      shiftStart.getTime() +
        Math.floor((shiftEnd.getTime() - shiftStart.getTime()) / 2),
    );
    const breakStartTime = new Date(
      midpoint.getTime() - durationMinutes * 30 * 1000,
    );
    const breakEndTime = new Date(
      breakStartTime.getTime() + durationMinutes * 60 * 1000,
    );

    this.form.controls.breakStartTime.setValue(breakStartTime, {
      emitEvent: false,
    });
    this.form.controls.breakEndTime.setValue(breakEndTime, {
      emitEvent: false,
    });
  }

  private getExistingBreakActivityWindow(): {
    start: Date;
    end: Date;
  } | null {
    const breakActivity = this.shift().activities.find(
      (activity) =>
        activity.activityCategoryId === ActivityCategoriesIdsMap.Break,
    );
    if (!breakActivity?.startDatetime || !breakActivity?.endDatetime) {
      return null;
    }

    return {
      start: new Date(breakActivity.startDatetime),
      end: new Date(breakActivity.endDatetime),
    };
  }

  private resolveBreakDurationMinutes(
    previousOption: SimplifiedShiftBreakOption,
  ): 30 | 60 | null {
    if (previousOption === 30 || previousOption === 60) {
      return previousOption;
    }

    const selectedShift = this.shift();
    const breakActivity = selectedShift.activities.find(
      (activity) =>
        activity.activityCategoryId === ActivityCategoriesIdsMap.Break,
    );
    if (!breakActivity) {
      return null;
    }

    const durationMinutes = Math.round(
      (new Date(breakActivity.endDatetime).getTime() -
        new Date(breakActivity.startDatetime).getTime()) /
        (1000 * 60),
    );

    return durationMinutes === 30 || durationMinutes === 60
      ? durationMinutes
      : null;
  }

  protected setLabels(labels: Label[]): void {
    this.form.controls.labels.markAsDirty();
    this.form.controls.labels.setValue(labels);
  }

  protected setProjectId(projectId: string): void {
    this.form.controls.projectId.markAsDirty();
    this.form.controls.projectId.setValue(projectId || null);
  }

  protected setAgreementId(agreement: SelectedAgreementOutput | null): void {
    this.formService.clearMixedAgreements();
    this.form.controls.agreementId.markAsDirty();
    this.form.controls.agreementId.setValue(agreement?.agreementId ?? null);
  }
}
